import Util from 'bootstrap/js/dist/util';
import config from 'lib/config';
import DateFns from 'lib/datefns';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import set from 'lodash/set';
import union from 'lodash/union';
import uniqBy from 'lodash/uniqBy';
import groupBy from 'lodash/groupBy';
import flatten from 'lodash/flatten';
import flattenDeep from 'lodash/flattenDeep';
import remove from 'lodash/remove';
import intersection from 'lodash/intersection';
import difference from 'lodash/difference';
import pull from 'lodash/pull';
import without from 'lodash/without';
import findKey from 'lodash/findKey';
import partition from 'lodash/partition';
import findIndex from 'lodash/findIndex';
import concat from 'lodash/concat';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';
import truncate from 'lodash/truncate';
import { QueryString } from '@borvik/querystring';
import { format, parse, isAfter, isMatch, differenceInDays } from 'date-fns';

export const TRANSITION_END = Util.TRANSITION_END;
export const getTransitionDuration = Util.getTransitionDurationFromElement;

export const dateFormat = DateFns.format;
export const dateFormatTZ = DateFns.formatTZ;

export const resourceUri = path => {
  const uri = config.resource.baseUri;
  const slash = uri.endsWith('/') || path.startsWith('/') ? '' : '/';

  return uri + slash + path;
};

export const assetUri = path => {
  const uri = config.public.assets.uri === '/' ? '' : config.public.assets.uri;
  const slash = uri.endsWith('/') || path.startsWith('/') ? '' : '/';
  return uri + slash + path;
};

export const baseUri = (path, query) => {
  const uri = config.public.baseUri === '/' ? '' : config.public.baseUri;
  const slash = uri.endsWith('/') || path.startsWith('/') ? '' : '/';

  if (query) query = toQuery(query);

  return uri + slash + path + (query ? `?${query}` : '');
};

export const apiUri = (path, query) => {
  const uri = config.api.uri;
  const slash = uri.endsWith('/') || path.startsWith('/') ? '' : '/';

  if (query) query = toQuery(query);

  return uri + slash + path + (query ? `?${query}` : '');
};

export const fullUri = (path, query) => `https://${window.AUTH.website.domain}${baseUri(path, query)}`;

// prettier-ignore
export const toCurrency = (value, def, options) => {
  const currency = window.AUTH.website.currency;

  if (isObject(def)) {
    options = def;
    def = undefined;
  }

  value = toFloat(value, def || 0);

  if (options?.show_words) {
    const digits = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tenths = ['zero', 'ten', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const [bills_unit, coins_unit] = (() => {
      switch(currency.code) {
        case 'EUR':
          return ['euro', 'cent'];

        case 'CAD':
        case 'USD':
        default:
          return ['dollar', 'cent'];
      }
    })();

    const [bills, coins] = value
      .toFixed(2)
      .split('.')
      .map(v => parseInt(v));

    const generate = (val, words = []) => {
      let word = '';
      let remainder = 0;

      if (val === 0) return words.length === 0 ? digits[0] : words.join(' ');

      if (val < 20) {
        remainder = 0;
        word = digits[val];
      } else if (val < 100) {
        remainder = val % 10;
        word = tenths[Math.floor(val / 10)];

        if (remainder) {
          word += '-' + digits[remainder];
          remainder = 0;
        }
      } else if (val < 1000) {
        remainder = val % 100;
        word = generate(Math.floor(val / 100)) + ' hundred';
      } else if (val < 1000000) {
        remainder = val % 1000;
        word = generate(Math.floor(val / 1000)) + ' thousand';
      } else if (val < 1000000000) {
        remainder = val % 1000000;
        word = generate(Math.floor(val / 1000000)) + ' million';
      }

      words.push(word);
      return generate(remainder, words);
    };

    return `${generate(bills)} ${bills_unit}${bills === 1 ? '': 's'} and ${generate(coins)} ${coins_unit}${coins === 1 ? '': 's'}`;
  }

  value = value.toLocaleString('en-US', {
    style: 'currency',
    currency: currency.code,
  });

  if (options?.show_code) value += `<span>${currency.code}</span>`;

  return value;
};

export const toInt = (value, def) => {
  if (value === null || value === undefined) value = def;
  if (isString(value)) value = value.replace(/[^0-9.-]/g, '');

  let result = parseInt(value);
  return isNaN(result) ? (def === undefined ? 0 : def) : result;
};

export const toFloat = (value, def) => {
  if (value === null || value === undefined) value = def;
  if (isString(value)) value = value.replace(/[^0-9.-]/g, '');

  let result = parseFloat(value);
  return isNaN(result) ? (def === undefined ? 0 : def) : result;
};

export const toBoolean = (value, def) => {
  if (value === null || value === undefined) value = def;
  if (value === undefined) value = false;

  value = value.toString().trim().toLowerCase();

  switch (value) {
    case 'true':
    case 't':
    case 'yes':
    case 'y':
    case '1':
      return true;

    case 'false':
    case 'f':
    case 'no':
    case 'n':
    case '0':
      return false;
  }

  return false;
};

export const pluralize = (value, word) => {
  return `${value} ${word}${value === 1 ? '' : 's'}`;
};

export const isNumber = value => {
  return typeof value === 'number';
};

export const isString = value => {
  return typeof value === 'string';
};

export const isArray = value => {
  return value && Array.isArray(value);
};

export const isObject = value => {
  return value && typeof value === 'object' && !Array.isArray(value);
};

export const isJSON = value => {
  try {
    if (!isString(value)) return false;
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
};

export const isEmpty = value => {
  if (isArray(value)) return value.length === 0;
  if (isString(value)) return value.trim().length === 0;
  if (isObject(value)) return Object.entries(value).length === 0;
  if (isNumber(value)) return false;

  return !value;
};

export const fromJSON = (value, def) => {
  try {
    if (isString(value)) return JSON.parse(value);
    return def;
  } catch (e) {
    return def;
  }
};

export const UUID = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const toArray = value => {
  return [].concat(value).filter(Boolean);
};

export const toQuery = (value, options) => {
  return QueryString.stringify(value, options);
};

export const parseQuery = value => {
  return QueryString.parse(value);
};

export const getEnum = (enumType, value) => {
  if (typeof value === 'undefined' || value == null) return null;

  for (const key in enumType) {
    if (enumType[key].value == value) return enumType[key];
  }

  return null;
};

export const removeNullProps = value => {
  Object.keys(value).forEach(key => {
    let data = value[key];
    if (isObject(data) && Object.keys(data).length > 0) removeNullProps(data);
    if (data === null || data === undefined) delete value[key];
  });

  return value;
};

export const containsUpperCase = (value, quantity) => {
  if (!quantity) quantity = 1;
  const valid = [];

  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const unicode = value.charCodeAt(i);

    if (char == char.toUpperCase() && unicode < 91 && unicode > 64) {
      valid.push(true);
    }
  }
  if (valid.length >= quantity) return true;
  else return false;
};

export const containsLowerCase = (value, quantity) => {
  if (!quantity) quantity = 1;
  const valid = [];

  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const unicode = value.charCodeAt(i);

    if (char == char.toLowerCase() && unicode < 123 && unicode > 96) {
      valid.push(true);
    }
  }

  if (valid.length >= quantity) return true;
  else return false;
};

export const containsNumber = (value, quantity) => {
  if (!quantity) quantity = 1;
  const valid = [];

  for (var i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const unicode = value.charCodeAt(i);

    if (unicode >= 48 && unicode <= 57) {
      valid.push(true);
    }
  }

  if (valid.length >= quantity) return true;
  else return false;
};

// supports these special characters ! " # $ % & ' ( ) * + / < = > ? @
export const containsSpecialCharacter = (value, quantity) => {
  if (!quantity) quantity = 1;
  const valid = [];

  for (let i = 0; i < value.length; i++) {
    const unicode = value.charCodeAt(i);

    if (unicode < 44 || unicode === 47 || (unicode > 60 && unicode < 65)) {
      valid.push(true);
    }
  }

  if (valid.length >= quantity) return true;
  else return false;
};

export const freeze = o => {
  Object.keys(o).forEach(p => {
    p = o[p];
    if (typeof p === 'object' && !Object.isFrozen(p)) freeze(p);
  });

  return Object.freeze(o);
};

export const thaw = o => {
  o = Array.isArray(o) ? [...o] : Object.assign({}, o);

  Object.keys(o).forEach(p => {
    const v = o[p];
    if (typeof v === 'object' && Object.isFrozen(v)) o[p] = thaw(v);
  });

  return o;
};

export const toSize = (bytes, precision = 1) => {
  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const t = 1024;
  let u = -1;

  if (Math.abs(bytes) < t) return bytes + ' B';
  do {
    bytes /= t;
    ++u;
  } while (Math.abs(bytes) >= t && u < units.length - 1);

  return `${bytes.toFixed(precision)} ${units[u]}`;
};

export function getNestedKey(obj, keyToFind) {
  if (obj[`${keyToFind}`]) {
    return obj[`${keyToFind}`];
  }

  for (const key in obj) {
    if (isObject(obj[key])) {
      const result = getNestedKey(obj[key], keyToFind);
      if (result) {
        return result;
      }
    }
  }

  return null;
}

export {
  capitalize,
  set,
  get,
  merge,
  pick,
  isEqual,
  union,
  uniqBy,
  groupBy,
  flatten,
  flattenDeep,
  concat,
  findKey,
  remove,
  intersection,
  difference,
  pull,
  without,
  partition,
  findIndex,
  cloneDeep,
  omit,
  omitBy,
  format,
  parse,
  isAfter,
  isMatch,
  differenceInDays,
  sortBy,
  sumBy,
  truncate,
};
